import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CategoryService } from './../../../../services/site/category.service';
import { SubcategoryUpdate_v2 } from 'src/app/models/category.model';

export interface Subcategory {
  id: number;
  name: string;
  activeFlag: boolean;
  sortOrder: number;
  categoryId: number;
  slug?: string;
  description?: string;
  createdDate?: Date;
  updatedDate?: Date;
}

export interface SubcategoryFormData {
  name: string;
  activeFlag: boolean;
  sortOrder: number;
  slug?: string;
  description?: string;
}

@Component({
  selector: 'app-admin-subcategory-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './admin-subcategory-modal.component.html',
  styleUrl: './admin-subcategory-modal.component.scss'
})
export class AdminSubcategoryModalComponent implements OnInit, OnChanges {
  @Input() subcategory: SubcategoryUpdate_v2 | null = null;
  @Input() categoryId!: number;
  @Input() isVisible = false;
  @Input() existingSubcategories: SubcategoryUpdate_v2[] = [];
  @Input() isEditMode = false;
  
  @Output() save = new EventEmitter<SubcategoryFormData>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<void>();
  @Output() delete = new EventEmitter<SubcategoryUpdate_v2>();
  @Output() toggleStatus = new EventEmitter<SubcategoryUpdate_v2>();

  subcategoryForm!: FormGroup;
  isSubmitting = false;
  showDeleteConfirm = false;
  formErrors: { [key: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private categoryService: CategoryService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadSubcategoryData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subcategory'] && this.subcategoryForm) {
      this.loadSubcategoryData();
    }
    if (changes['isVisible'] && changes['isVisible'].currentValue) {
      this.resetForm();
      this.loadSubcategoryData();
    }
  }

  private initializeForm(): void {
    this.subcategoryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      activeFlag: [true],
      sortOrder: [0, [Validators.min(0), Validators.max(999)]],
      slug: ['', [Validators.pattern(/^[a-z0-9-]*$/)]],
      description: ['', [Validators.maxLength(250)]]
    });

    // Auto-generate slug from name for new subcategories
    this.subcategoryForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.isEditMode) {
        const slug = this.generateSlug(name);
        this.subcategoryForm.patchValue({ slug }, { emitEvent: false });
      }
    });

    // Add custom validator for unique names
    this.subcategoryForm.get('name')?.addValidators(this.uniqueNameValidator.bind(this));
  }

  private loadSubcategoryData(): void {
    if (this.subcategory && this.isEditMode) {
      this.subcategoryForm.patchValue({
        name: this.subcategory.name,
        activeFlag: this.subcategory.activeFlag,
        sortOrder: this.subcategory.sortOrder,
        //slug: this.subcategory.slug || this.generateSlug(this.subcategory.name),
        //description: this.subcategory.description || ''
        slug: this.generateSlug(this.subcategory.name),
        description: ''
      });
    } else if (!this.isEditMode) {
      // Set default values for new subcategory
      const maxSortOrder = this.existingSubcategories.length > 0 
        ? Math.max(...this.existingSubcategories.map(s => s.sortOrder))
        : -1;
      
      this.subcategoryForm.patchValue({
        name: '',
        activeFlag: true,
        sortOrder: maxSortOrder + 1,
        slug: '',
        description: ''
      });
    }
  }

  onSubmit(): void {
    if (this.subcategoryForm.valid) {
      this.isSubmitting = true;
      const formData: SubcategoryFormData = this.subcategoryForm.value;
      console.log('Form submitted with data:', formData);
      
      const subcategory: SubcategoryUpdate_v2 = {
        id: this.subcategory ? this.subcategory.id : 0,
        name: formData.name,
        slug: formData.slug || this.generateSlug(formData.name),
        parentCategoryId: this.categoryId,
        activeFlag: formData.activeFlag,
        sortOrder: formData.sortOrder
      };

      this.categoryService.upsertSubcategory(this.categoryId, subcategory)
        .subscribe({
          next: (data) => {
            console.log('Successfully updated subcategory');
            this.save.emit(formData);
            this.isSubmitting = false;
            this.onClose();
          },
          error: (err) => {
            console.error('UpsertSubcategorys Error: ' + err);
          }
        })
      
    } else {
      this.markFormGroupTouched();
      this.updateFormErrors();
    }
  }

  onClose(): void {
    this.close.emit();
    this.resetForm();
    this.showDeleteConfirm = false;
  }

  onToggleStatus(): void {
    if (this.subcategory && this.isEditMode) {
      this.toggleStatus.emit(this.subcategory);
      this.onClose();
    }
  }

  onDelete(): void {
    this.showDeleteConfirm = true;
  }

  confirmDelete(): void {
    if (this.subcategory && this.isEditMode) {
      this.delete.emit(this.subcategory);
      this.showDeleteConfirm = false;
      this.onClose();
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget && !this.showDeleteConfirm) {
      this.onClose();
    }
  }

  private resetForm(): void {
    this.subcategoryForm.reset();
    this.formErrors = {};
    this.isSubmitting = false;
    this.showDeleteConfirm = false;
  }

  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  private uniqueNameValidator(control: any) {
    if (!control.value) return null;
    
    const existingNames = this.existingSubcategories
      .filter(s => this.subcategory && this.isEditMode ? s.id !== this.subcategory.id : true)
      .map(s => s.name.toLowerCase());
    
    const isDuplicate = existingNames.includes(control.value.toLowerCase());
    return isDuplicate ? { uniqueName: true } : null;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.subcategoryForm.controls).forEach(key => {
      const control = this.subcategoryForm.get(key);
      control?.markAsTouched();
    });
  }

  private updateFormErrors(): void {
    this.formErrors = {};
    Object.keys(this.subcategoryForm.controls).forEach(key => {
      const control = this.subcategoryForm.get(key);
      if (control?.errors && control.touched) {
        this.formErrors[key] = this.getFieldError(key);
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.subcategoryForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.subcategoryForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required.`;
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters.`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} must not exceed ${field.errors['maxlength'].requiredLength} characters.`;
      if (field.errors['pattern']) return `${this.getFieldLabel(fieldName)} contains invalid characters.`;
      if (field.errors['min']) return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['min'].min}.`;
      if (field.errors['max']) return `${this.getFieldLabel(fieldName)} must not exceed ${field.errors['max'].max}.`;
      if (field.errors['uniqueName']) return `${this.getFieldLabel(fieldName)} already exists.`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Subcategory name',
      sortOrder: 'Sort order',
      slug: 'URL slug',
      description: 'Description'
    };
    return labels[fieldName] || fieldName;
  }

  get isFormValid(): boolean {
    return this.subcategoryForm.valid;
  }

  get modalTitle(): string {
    return this.isEditMode 
      ? `Edit Subcategory: ${this.subcategory?.name}` 
      : 'Add New Subcategory';
  }

  get submitButtonText(): string {
    if (this.isSubmitting) {
      return this.isEditMode ? 'Updating...' : 'Adding...';
    }
    return this.isEditMode ? 'Update Subcategory' : 'Add Subcategory';
  }
}